"use client";

import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { channelsAPI, handleApiError } from "../../services/api";
import "./ChannelInvitationsCard.css";

const ChannelInvitationsCard = ({ onAccepted }) => {
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState({});
  const isRequestInFlight = useRef(false);

  const loadInvitations = async () => {
    if (isRequestInFlight.current) return;
    
    isRequestInFlight.current = true;
    setIsLoading(true);
    try {
      const response = await channelsAPI.getChannelInvitations({
        page: 1,
        per_page: 10,
      });

      if (response?.data?.data) {
        const items = response.data.data.map((invitation) => {
          const channel = invitation.channel || {};
          return {
            id: invitation.id,
            channelId: channel.id,
            name: channel.name,
            description: channel.description,
            profileImage:
              channel.profile_image_url ||
              channel.profile_image ||
              (channel.images && channel.images.profile) ||
              "/images/social/pic-placeholder.png",
            privacy:
              channel.privacy_setting ||
              channel.privacySetting ||
              "public",
            status: invitation.status,
            expiresAt: invitation.expires_at || null,
          };
        });
        setInvitations(items);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
      isRequestInFlight.current = false;
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  const handleAccept = async (invitationId, channelId) => {
    setProcessingIds((prev) => ({ ...prev, [invitationId]: true }));
    try {
      await channelsAPI.acceptInvitation(invitationId);
      setInvitations((prev) =>
        prev.filter((inv) => inv.id !== invitationId)
      );
      if (onAccepted) {
        onAccepted(channelId);
      }
      // Dispatch event to refresh channels in sidebar
      window.dispatchEvent(new CustomEvent('channel:invitation-accepted', {
        detail: { channelId, invitationId }
      }));
    } catch (error) {
      handleApiError(error);
    } finally {
      setProcessingIds((prev) => ({ ...prev, [invitationId]: false }));
    }
  };

  const handleDecline = async (invitationId) => {
    setProcessingIds((prev) => ({ ...prev, [invitationId]: true }));
    try {
      await channelsAPI.declineInvitation(invitationId);
      setInvitations((prev) =>
        prev.filter((inv) => inv.id !== invitationId)
      );
    } catch (error) {
      handleApiError(error);
    } finally {
      setProcessingIds((prev) => ({ ...prev, [invitationId]: false }));
    }
  };

  if (isLoading && invitations.length === 0) {
    return (
      <div className="channel-invitations-card">
        <div className="channel-invitations-header">
          <h3>{t("channelList.invitations.title", "Community invitations")}</h3>
        </div>
        <div className="channel-invitations-body loading">
          <div className="loading-spinner" />
          <p>{t("channelList.invitations.loading", "Loading invitations...")}</p>
        </div>
      </div>
    );
  }

  if (!isLoading && invitations.length === 0) {
    return null;
  }

  return (
    <div className="channel-invitations-card">
      <div className="channel-invitations-header">
        <h3>{t("channelList.invitations.title", "Community invitations")}</h3>
        <span className="channel-invitations-count">
          {invitations.length}
        </span>
      </div>
      <div className="channel-invitations-body">
        {invitations.map((invitation) => {
          const isPrivate = invitation.privacy === "private";
          const isProcessing = processingIds[invitation.id];

          return (
            <div
              key={invitation.id}
              className="channel-invitations-item"
            >
              <div className="channel-invitations-info">
                <img
                  src={invitation.profileImage}
                  alt={invitation.name}
                  className="channel-invitations-avatar"
                  onError={(e) => {
                    e.target.src = "/images/social/pic-placeholder.png";
                  }}
                />
                <div className="channel-invitations-text">
                  <div className="channel-invitations-name-row">
                    <span className="channel-invitations-name">
                      {invitation.name}
                    </span>
                    {isPrivate && (
                      <span className="channel-invitations-privacy-badge">
                        {t("channelHeader.private", "Private")}
                      </span>
                    )}
                  </div>
                  {invitation.description && (
                    <p className="channel-invitations-description">
                      {invitation.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="channel-invitations-actions">
                <button
                  className="channel-invitations-decline"
                  onClick={() => handleDecline(invitation.id)}
                  disabled={isProcessing}
                >
                  {t("channelList.invitations.decline", "Decline")}
                </button>
                <button
                  className="channel-invitations-accept"
                  onClick={() =>
                    handleAccept(invitation.id, invitation.channelId)
                  }
                  disabled={isProcessing}
                >
                  {t("channelList.invitations.accept", "Accept")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChannelInvitationsCard;


